import type { FilterOption } from '@/components/FilterMenu'
import type { DeploymentLabelOption } from '@/contexts/useApiaries'
import { useMemo } from 'react'

export enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc',
}

export interface ClusterConfigFilterParams {
  page: number
  selectedSortOption?: FilterOption | null
  selectedDeploymentLabel?: DeploymentLabelOption | null
  selectedTemplate?: FilterOption | null
  includeDisabled?: boolean
  selectedOrgId?: string | null
  selectedApprovalState?: FilterOption | null
  selectedHiveState?: FilterOption | null
  selectedTags?: FilterOption[]
  selectedHiveId?: string | null
  compact?: boolean
}

export function useClusterConfigFilters(
  filterParams: ClusterConfigFilterParams,
) {
  const {
    page,
    selectedSortOption,
    selectedDeploymentLabel,
    selectedTemplate,
    includeDisabled,
    selectedOrgId,
    selectedApprovalState,
    selectedHiveState,
    selectedTags,
    selectedHiveId,
    compact,
  } = filterParams

  // Enhanced query parameters that include filter values
  const enhancedQueryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page,
      order: selectedSortOption?.value || SortOrder.Descending, // Use the selected sort option
    }

    // Add deployment label filter if selected
    if (selectedDeploymentLabel) {
      params.apiaryId = selectedDeploymentLabel.value
    }

    // Add organization filter if selected
    if (selectedOrgId) {
      params.orgId = selectedOrgId
    }

    params.includeDisabled = includeDisabled || false

    if (selectedTemplate) {
      params.templateId = selectedTemplate.value
    }

    // Add approval state filter if selected
    if (selectedApprovalState) {
      params.approvalState = selectedApprovalState.value
    }

    // Add hive state filter if selected
    if (selectedHiveState) {
      params.hiveState = selectedHiveState.value
    }

    // Add selected tags if any
    if (selectedTags && selectedTags.length > 0) {
      params.tags = selectedTags.map((tag) => tag.value).join(',')
    }

    // Add hiveId filter if selected
    if (selectedHiveId) {
      params.hiveId = selectedHiveId
    }

    params.compact = compact || false

    return params
  }, [
    page,
    selectedSortOption,
    selectedDeploymentLabel,
    selectedTemplate,
    includeDisabled,
    selectedOrgId,
    selectedApprovalState,
    selectedHiveState,
    selectedTags,
    selectedHiveId,
    compact,
  ])

  // Check if there are any active filters (anything but sort and includeDisabled are considered filters)
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        selectedDeploymentLabel ||
          selectedTemplate ||
          selectedOrgId ||
          selectedApprovalState ||
          selectedHiveState ||
          (selectedTags && selectedTags.length > 0) ||
          selectedHiveId,
      ),
    [
      selectedDeploymentLabel,
      selectedTemplate,
      selectedOrgId,
      selectedApprovalState,
      selectedHiveState,
      selectedTags,
      selectedHiveId,
    ],
  )

  return {
    enhancedQueryParams,
    hasActiveFilters,
  }
}
